<template>
  <div>
    <div class="page-header">
      <div class="add-item d-flex">
        <div class="page-title">
          <h4>{{ $t('edit_role') }}</h4>
        </div>
      </div>
      <ul class="table-top-head">
        <li>
          <div class="page-btn">
            <router-link to="/roles" class="btn btn-added">
              <span>Back</span>
            </router-link>
          </div>
        </li>
        <li>
          <a data-bs-toggle="tooltip" data-bs-placement="top" title="Collapse" id="collapse-header"><i class="ti ti-chevron-up"></i></a>
        </li>
      </ul>
    </div>

    <form @submit.prevent="saveRole" enctype="multipart/form-data">
      <div class="card">
        <div class="card-body add-product pb-0">
          <div class="accordion-card-one accordion" id="accordionExample">
            <div class="accordion-item">
              <div id="collapseOne" class="accordion-collapse collapse show" aria-labelledby="headingOne" data-bs-parent="#accordionExample">
                <div class="accordion-body">
                  <div class="row">
                    <div class="col-lg-6 col-md-6 mb-3">
                      <label class="form-label">{{$t('role_name')}} <span class="text-danger">*</span></label>
                      <input
                        type="text"
                        v-model="form.name"
                        class="form-control"
                        :placeholder="$t('enter_role_name')"
                      />
                      <div v-if="errors.name" class="text-danger mt-1">{{ errors.name }}</div>
                    </div>

                    <div class="col-lg-6 col-md-6 mb-3">
                      <label class="form-label">{{$t('description')}} </label>
                      <input
                        type="text"
                        v-model="form.description"
                        class="form-control"
                        :placeholder="$t('enter_description')"
                      />
                      <div v-if="errors.description" class="text-danger mt-1">{{ errors.description }}</div>
                    </div>

                    <div class="col-md-6">
                      <div class="d-flex align-items-center">
                        <label class="form-label small mb-0 me-3" style="width: 90px;" >{{ $t('user_type') }} <span class="text-danger">*</span></label>
                        <FormDropdown
                          v-model="form.user_type"
                          :options="formattedusertypes"
                          clearable
                          class="flex-grow-1"
                        />
                      </div>
                      <div class="text-danger mt-1" style="height:0.75em; margin-left: 150px; font-size:0.875rem;">
                        <span v-if="errors.user_type">{{ errors.user_type }}</span>
                      </div>
                    </div>

                    <permission-tree
                      v-if="ready"
                      :key="treeKey"
                      :categories="categories"
                      :selected="selectedPerms"
                      :user-type-permissions="userTypePermissions"
                      :user-type="userTypeKey"
                      @update="updatePermission"
                    />
                    <div v-if="errors.role_permissions" class="text-danger mb-3">
                      <div v-for="msg in [].concat(errors.role_permissions)" :key="msg">{{ msg }}</div>
                    </div>

                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-lg-12 text-end">
        <div class="btn-addproduct mb-4">
          <button type="button" class="btn btn-cancel me-2" @click="cancelForm">{{ $t('cancel') }}</button>
          <button type="submit" class="btn btn-submit" @click="onSubmit">{{ $t('submit') }}</button>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import roleApi from "@/api/roles";
import permApi from "@/api/permissions";
import PermissionTree from "@/components/permissions/PermissionTree.vue";
import { useToast } from "vue-toastification";

export default {
  components: {
    PermissionTree,
  },

  setup() {
    const toast = useToast();
    return { toast };
  },

  data() {
    return {
      roleId: null,
      treeKey: 0,
      ready: false,

      form: {
        name: "",
        user_type: null,
        description: "",
        role_permissions: [],
      },

      userTypes: [],
      categories: [],
      selectedPerms: {},
      userTypePermissions: [],

      errors: {},
      loading: false,
    };
  },

  async created() {
    this.roleId = this.$route.params.id;

    // Load user types
    const ut = await this.$axios.get("/user/user-types/");
    this.userTypes = ut.data;

    // Load permission categories
    const pc = await permApi.categories();
    this.categories = pc.data;

    // Load role data
    await this.loadRole();
  },

  watch: {
    // Watch for user type changes
    async "form.user_type"(newVal) {
      if (!newVal) {
        this.userTypePermissions = [];
        this.selectedPerms = {};
        this.treeKey++;
        return;
      }

      await this.loadPermissionsForUserType(newVal);
      this.treeKey++;
    },
  },

  computed: {
    formattedusertypes() {
      return this.userTypes.map(u => ({
        id: u.id,
        text: u.name,
      }));
    },

    userTypeKey() {
      if (!this.form.user_type) return null;
      const ut = this.userTypes.find(u => u.id == this.form.user_type);
      return ut ? ut.key.toLowerCase() : null;
    },
  },

  methods: {
    async loadPermissionsForUserType(userTypeId) {
      try {
        const res = await this.$axios.get(
          `/user/permissions/user-type/${userTypeId}/`
        );

        // Store the flat list of permission items
        this.userTypePermissions = res.data;

        // Filter out any selected permissions that are not allowed for this user type
        const allowedIds = res.data.map(p => p.id);
        Object.keys(this.selectedPerms).forEach(permId => {
          if (!allowedIds.includes(Number(permId))) {
            delete this.selectedPerms[permId];
          }
        });
      } catch (error) {
        console.error("Failed to load permissions:", error);
        this.userTypePermissions = [];
      }
    },

    async loadRole() {
      try {
        this.ready = false;

        const res = await roleApi.get(this.roleId);
        const role = res.data;

        // Basic fields
        this.form.name = role.name;
        this.form.description = role.description;
        this.form.user_type = role.user_type;

        // Load allowed permissions for user type
        await this.loadPermissionsForUserType(role.user_type);

        // Apply role permissions - CORRECTED STRUCTURE
        this.selectedPerms = {};
        if (Array.isArray(role.role_permissions)) {
          role.role_permissions.forEach(p => {
            if (p.permission_item && p.permission_item.id) {
              const permId = Number(p.permission_item.id);
              // Only add if this permission is in the user type permissions
              if (this.userTypePermissions.some(up => up.id === permId)) {
                this.selectedPerms[permId] = p.access_level || "N";
              }
            }
          });
        }

        console.log("Selected Permissions:", this.selectedPerms);

        // Force tree to re-render
        await this.$nextTick();
        this.treeKey++;
        this.ready = true;

      } catch (e) {
        console.error("Failed to load role:", e);
        this.toast.error("Failed to load role");
        this.$router.push("/roles");
      }
    },

    updatePermission({ id, level }) {
      const permId = Number(id);
      if (level === "N") {
        delete this.selectedPerms[permId];
      } else {
        this.selectedPerms[permId] = level;
      }
    },

    async saveRole() {
      this.loading = true;
      this.errors = {};

      if (!this.form.name.trim()) {
        this.errors.name = this.$t("error.required");
      }
      if (!this.form.user_type) {
        this.errors.user_type = this.$t("error.required");
      }

      if (Object.keys(this.errors).length) {
        this.loading = false;
        return;
      }

      // Convert selected permissions to array - CORRECTED FOR API
      this.form.role_permissions = Object.entries(this.selectedPerms)
        .map(([permission_item_id, access_level]) => ({
          permission_item_id: Number(permission_item_id),
          access_level: access_level,
        }));

      try {
        await roleApi.update(this.roleId, this.form);
        this.toast.success("Role successfully updated!");
        this.$router.push("/roles");
      } catch (error) {
        if (error.response?.status === 400) {
          this.errors = error.response.data;
        } else {
          this.toast.error(this.$t("something_went_wrong"));
        }
      } finally {
        this.loading = false;
      }
    },

    cancelForm() {
      this.$router.push("/roles");
    },
  },
};
</script>

<style scoped>
/* Add loading indicator style */
.loading-overlay {
  position: relative;
  min-height: 200px;
}

.loading-overlay::after {
  content: "Loading permissions...";
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(255, 255, 255, 0.9);
  padding: 10px 20px;
  border-radius: 4px;
  z-index: 10;
}
</style>


<!--<script>-->
<!--import roleApi from "@/api/roles";-->
<!--import permApi from "@/api/permissions";-->
<!--import PermissionTree from "@/components/permissions/PermissionTree.vue";-->
<!--import VueSelect2 from "vue3-select2-component";-->
<!--import { useToast } from "vue-toastification";-->

<!--export default {-->
<!--  components: {-->
<!--    PermissionTree,-->
<!--    VueSelect2,-->
<!--  },-->

<!--  setup() {-->
<!--    const toast = useToast();-->
<!--    return { toast };-->
<!--  },-->

<!--  data() {-->
<!--    return {-->
<!--      roleId: null,-->
<!--      treeKey: 0,-->
<!--      ready: false,-->

<!--      form: {-->
<!--        name: "",-->
<!--        user_type: null,-->
<!--        description: "",-->
<!--        role_permissions: [],-->
<!--      },-->

<!--      userTypes: [],-->
<!--      categories: [],-->
<!--      selectedPerms: {},-->
<!--      userTypePermissions: [],-->

<!--      errors: {},-->
<!--      loading: false,-->
<!--    };-->
<!--  },-->

<!--  async created() {-->
<!--    this.roleId = this.$route.params.id;-->

<!--    // Load user types-->
<!--    const ut = await this.$axios.get("/user/user-types/");-->
<!--    this.userTypes = ut.data;-->

<!--    // Load permission categories-->
<!--    const pc = await permApi.categories();-->
<!--    this.categories = pc.data;-->

<!--    // Load role data-->
<!--    await this.loadRole();-->
<!--  },-->

<!--  // watch: {-->
<!--  //   async "form.user_type"(newVal) {-->
<!--  //     if (!newVal) {-->
<!--  //       this.userTypePermissions = [];-->
<!--  //       return;-->
<!--  //     }-->
<!--  //-->
<!--  //     const res = await this.$axios.get(-->
<!--  //       `/user/permissions/user-type/${newVal}/`-->
<!--  //     );-->
<!--  //-->
<!--  //     this.userTypePermissions = res.data;-->
<!--  //   },-->
<!--  // },-->

<!--  computed: {-->
<!--    formattedusertypes() {-->
<!--      return this.userTypes.map(u => ({-->
<!--        id: u.id,-->
<!--        text: u.name,-->
<!--      }));-->
<!--    },-->

<!--    userTypeKey() {-->
<!--      if (!this.form.user_type) return null;-->
<!--      const ut = this.userTypes.find(u => u.id == this.form.user_type);-->
<!--      return ut ? ut.key.toLowerCase() : null;-->
<!--    },-->
<!--  },-->

<!--  methods: {-->
<!--    async loadRole() {-->
<!--  try {-->
<!--    this.ready = false; // stop tree rendering-->

<!--    const res = await roleApi.get(this.roleId);-->
<!--    const role = res.data;-->

<!--    // Basic fields-->
<!--    this.form.name = role.name;-->
<!--    this.form.description = role.description;-->
<!--    this.form.user_type = role.user_type;-->

<!--    // Load allowed permissions for user type-->
<!--    const permRes = await this.$axios.get(-->
<!--      `/user/permissions/user-type/${role.user_type}/`-->
<!--    );-->
<!--    this.userTypePermissions = permRes.data;-->

<!--    // Apply role permissions-->
<!--    this.selectedPerms = {};-->
<!--    role.role_permissions.forEach(p => {-->
<!--      this.selectedPerms[p.permission_item_id] = p.access_level;-->
<!--    });-->

<!--    // Wait for Vue reactivity to settle-->
<!--    await this.$nextTick();-->

<!--    // Force clean remount-->
<!--    this.treeKey++;-->
<!--    this.ready = true;-->

<!--  } catch (e) {-->
<!--    this.toast.error("Failed to load role");-->
<!--    this.$router.push("/roles");-->
<!--  }-->
<!--},-->


<!--    updatePermission({ id, level }) {-->
<!--      this.selectedPerms[Number(id)] = level;-->
<!--    },-->

<!--    async saveRole() {-->
<!--      this.loading = true;-->
<!--      this.errors = {};-->

<!--      if (!this.form.name.trim()) {-->
<!--        this.errors.name = this.$t("error.required");-->
<!--      }-->
<!--      if (!this.form.user_type) {-->
<!--        this.errors.user_type = this.$t("error.required");-->
<!--      }-->

<!--      if (Object.keys(this.errors).length) {-->
<!--        this.loading = false;-->
<!--        return;-->
<!--      }-->

<!--      this.form.role_permissions = Object.entries(this.selectedPerms)-->
<!--        .map(([permission_item_id, access_level]) => ({-->
<!--          permission_item_id: Number(permission_item_id),-->
<!--          access_level: access_level || "N",-->
<!--        }));-->

<!--      try {-->
<!--        await roleApi.update(this.roleId, this.form);-->
<!--        this.toast.success("Role successfully updated!");-->
<!--        this.$router.push("/roles");-->
<!--      } catch (error) {-->
<!--        if (error.response?.status === 400) {-->
<!--          this.errors = error.response.data;-->
<!--        } else {-->
<!--          this.toast.error(this.$t("something_went_wrong"));-->
<!--        }-->
<!--      } finally {-->
<!--        this.loading = false;-->
<!--      }-->
<!--    },-->

<!--    cancelForm() {-->
<!--      this.$router.push("/roles");-->
<!--    },-->
<!--  },-->
<!--};-->
<!--</script>-->
